import { AutoCompleteOption } from "../components/form/APAutoComplete";
import { PaginationResponse } from "./Pagination";

export type DossierTransactionType =
  | "fuel"
  | "servicing"
  | "tyres"
  | "rego"
  | "insurance"
  | "finance"
  | "fees"
  | "pre-tax"
  | "post-tax"
  | "invoiced";

export type DossierTransactionSpentType = Exclude<
  DossierTransactionType,
  "pre-tax" | "post-tax" | "invoiced"
>;

export type DossierTransactionStatus =
  | "unpaid"
  | "payment-in-progress"
  | "paid"
  | "cancelled";

export type DossierTransaction = {
  id: string;
  catcheTransactionId: string | null;
  driverId: string | null;
  type: DossierTransactionType;
  status: DossierTransactionStatus;
  lastSyncDate: string | null;
  incGst: number;
  exGst: number;
  invoiceId: string | null;
  dossierId: string;
  transactionDate: string;
  expenseClaimId: string | null;
  description: string | null;
  paidDate: string | null;
  createdDate: string;
  expenseClaimTaxInvoiceName: string | null;
  budgetsSet: boolean;
  financeBudget: number | null;
  insuranceBudget: number | null;
  feesBudget: number | null;
  fuelBudget: number | null;
  servicingBudget: number | null;
  tyresBudget: number | null;
  regoBudget: number | null;
};

export type DossierTransactionPaginationList =
  PaginationResponse<DossierTransaction>;

export const DossierTransactionTypeLabelMap: Record<
  DossierTransactionType,
  string
> = {
  fuel: "Fuel / Charging",
  servicing: "Service",
  tyres: "Tyres",
  rego: "Rego / CTP",
  insurance: "Insurance",
  finance: "Finance",
  fees: "Management Fee",
  "pre-tax": "Pre Tax",
  "post-tax": "Employee Contribution",
  invoiced: "Invoiced",
};

export const DossierTransactionTypeOptions: AutoCompleteOption[] = [
  { label: DossierTransactionTypeLabelMap.fuel, id: "fuel" },
  { label: DossierTransactionTypeLabelMap.servicing, id: "servicing" },
  { label: DossierTransactionTypeLabelMap.tyres, id: "tyres" },
  { label: DossierTransactionTypeLabelMap.rego, id: "rego" },
  { label: DossierTransactionTypeLabelMap.insurance, id: "insurance" },
  { label: DossierTransactionTypeLabelMap.finance, id: "finance" },
  { label: DossierTransactionTypeLabelMap.fees, id: "fees" },
  { label: DossierTransactionTypeLabelMap["pre-tax"], id: "pre-tax" },
  { label: DossierTransactionTypeLabelMap["post-tax"], id: "post-tax" },
];

export type TransactionComparisonMismatchedField = {
  ceValue: string | number | boolean | null;
  crmValue: string | number | boolean | null;
};

export type CatchETransactionType =
  | "Fuel"
  | "Tyres"
  | "Maintenance"
  | "Registration"
  | "Finance"
  | "Management Fee"
  | "Comprehensive Insurance"
  | "INS"
  | "FSL"
  | "INSFEE"
  | "ISD"
  | "Pre Tax"
  | "Post Tax";

export type DossierTransactionFromCatche = {
  contractId: string;
  driverId: string;
  invoiceNo: string | null;
  transactionId: string;
  lastModifiedDate: string;
  transactionDate: string;
  postedDate: string | null;
  status: DossierTransactionStatus;
  type: DossierTransactionType;
  catcheTransactionType: CatchETransactionType;
  description: string;
  amountIncGst: number;
  amountExGst: number;
  isReimbursement: boolean;
  supplierCode: string;
  supplierName: string;
};

export type TransactionComparison = {
  transactionId: string;
  status: "matching" | "mismatching" | "missing";
  ceTransaction: DossierTransactionFromCatche | null;
  crmTransaction: DossierTransaction | null;
  mismatchedFields: Partial<
    Record<keyof DossierTransaction, TransactionComparisonMismatchedField>
  >;
};

export const getDateForTransactionComparison = (
  transactionComparison: TransactionComparison
) => {
  const date =
    transactionComparison.ceTransaction?.transactionDate ||
    transactionComparison.crmTransaction?.transactionDate;
  return date ?? "";
};

export const getTransactionStatusForTransactionComparison = (
  transactionComparison: TransactionComparison
): DossierTransactionStatus | null => {
  return (
    transactionComparison.crmTransaction?.status ||
    transactionComparison.ceTransaction?.status ||
    null
  );
};

export const getTransactionTypeForTransactionComparison = (
  transactionComparison: TransactionComparison
): DossierTransactionType | null => {
  return (
    transactionComparison.crmTransaction?.type ||
    transactionComparison.ceTransaction?.type ||
    null
  );
};

export type TransactionComparisonSortOption = "date" | "status";

const transactionComparisonStatusSortOrder = [
  "missing",
  "mismatching",
  "matching",
];

export const filterTransactionComparisons = (
  comparisons: TransactionComparison[],
  transactionType: DossierTransactionType | null,
  transactionStatus: DossierTransactionStatus | null,
  comparisonStatus: "matching" | "mismatching" | "missing" | null
): TransactionComparison[] => {
  return comparisons.filter((comparison) => {
    const transactionTypeMatches = transactionType
      ? getTransactionTypeForTransactionComparison(comparison) ===
        transactionType
      : true;
    const transactionStatusMatches = transactionStatus
      ? getTransactionStatusForTransactionComparison(comparison) ===
        transactionStatus
      : true;
    const comparisonStatusMatches = comparisonStatus
      ? comparison.status === comparisonStatus
      : true;
    return (
      transactionTypeMatches &&
      transactionStatusMatches &&
      comparisonStatusMatches
    );
  });
};

export const sortTransactionComparisons = (
  comparisons: TransactionComparison[],
  transactionComparisonSortOptionId: TransactionComparisonSortOption
): TransactionComparison[] => {
  const comparisonsToSort = [...comparisons];

  let compareFunction: (
    a: TransactionComparison,
    b: TransactionComparison
  ) => number;

  switch (transactionComparisonSortOptionId) {
    case "date":
      compareFunction = (
        a: TransactionComparison,
        b: TransactionComparison
      ) => {
        const aDate = getDateForTransactionComparison(a);
        const bDate = getDateForTransactionComparison(b);
        if (aDate > bDate) {
          return -1;
        }
        if (aDate < bDate) {
          return 1;
        }
        return 0;
      };
      break;
    case "status":
      compareFunction = (
        a: TransactionComparison,
        b: TransactionComparison
      ) => {
        const aStatus = transactionComparisonStatusSortOrder.indexOf(a.status);
        const bStatus = transactionComparisonStatusSortOrder.indexOf(b.status);
        if (aStatus > bStatus) {
          return 1;
        }
        if (aStatus < bStatus) {
          return -1;
        }
        return 0;
      };
      break;
  }

  comparisonsToSort.sort(compareFunction);

  return comparisonsToSort;
};
